import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { setCurrencyState } from './shared';
import { DEFAULT_FX_NZD, fetchFxNzd } from '@/services/fxService';

// Display-currency provider. Source data is always USD; this just controls the
// presentation. Persists to localStorage so the operator's choice survives a
// reload. fmtUSD / fmtUSDc in shared.ts read from the module-level state we
// push here — so every callsite picks up the change at next render.
//
// Subscribing components call useCurrency() at the top of their render
// function. They don't need the returned value (the formatters work via
// module state), but the context subscription is what makes React re-render
// them when the user flips the currency or edits the rate.

// v2 keys: the dashboard default flipped USD → NZD (Arcanum bills in NZD).
// The provider persists on mount, so every earlier visitor had the old USD
// default written to the v1 keys even if they never touched the control —
// bumping the key re-defaults everyone to NZD once, then their own choices
// stick again.
const STORAGE_KEY_CODE = 'nd:currency:v2';
const STORAGE_KEY_RATE = 'nd:currencyRate:v2';
const STORAGE_KEY_PREFIX = 'nd:currencyPrefix:v2';

/** Default display currency: NZD at the shared portal FX default (live-refreshed
 *  on mount via fxService). Source data stays USD; operators can switch back to
 *  USD or edit the rate. */
const DEFAULT_CODE = 'NZD';
const DEFAULT_RATE = DEFAULT_FX_NZD;
const DEFAULT_PREFIX = 'NZ$';

// Rates we consider "not operator-chosen": the current fallback plus historical
// hardcoded defaults that older sessions persisted to localStorage. A live fetch
// replaces these; a genuinely hand-typed rate is left alone.
const STALE_DEFAULT_RATES = new Set([DEFAULT_FX_NZD, 1.65, 1.69]);

export interface CurrencyContextValue {
  code: string;
  rate: number;
  prefix: string;
  setCurrency: (code: string, rate: number, prefix: string) => void;
}

const DEFAULT: CurrencyContextValue = {
  code: DEFAULT_CODE,
  rate: DEFAULT_RATE,
  prefix: DEFAULT_PREFIX,
  setCurrency: () => {},
};

const CurrencyContext = createContext<CurrencyContextValue>(DEFAULT);

function readStored(): { code: string; rate: number; prefix: string } {
  const fallback = { code: DEFAULT_CODE, rate: DEFAULT_RATE, prefix: DEFAULT_PREFIX };
  if (typeof window === 'undefined') return fallback;
  try {
    const code = window.localStorage.getItem(STORAGE_KEY_CODE) || DEFAULT_CODE;
    const rate = Number(window.localStorage.getItem(STORAGE_KEY_RATE) || String(DEFAULT_RATE)) || DEFAULT_RATE;
    const prefix =
      window.localStorage.getItem(STORAGE_KEY_PREFIX) || (code === 'USD' ? '$' : code === 'NZD' ? 'NZ$' : code + '$');
    return { code, rate, prefix };
  } catch {
    return fallback;
  }
}

export function CurrencyProvider({ children }: { children: ReactNode }) {
  const [{ code, rate, prefix }, setState] = useState(readStored);

  // Push state into shared.ts on every change so the formatters see it.
  useEffect(() => {
    setCurrencyState({ code, rate, prefix });
    try {
      window.localStorage.setItem(STORAGE_KEY_CODE, code);
      window.localStorage.setItem(STORAGE_KEY_RATE, String(rate));
      window.localStorage.setItem(STORAGE_KEY_PREFIX, prefix);
    } catch {
      // localStorage might be disabled — fine, in-memory state still works.
    }
  }, [code, rate, prefix]);

  // Refresh to the live ECB reference rate once per session — but only when the
  // stored rate is a known default, so an operator's hand-typed rate sticks.
  useEffect(() => {
    let cancelled = false;
    void fetchFxNzd().then((fx) => {
      if (cancelled || !fx.live) return;
      setState((s) => (s.code === 'NZD' && STALE_DEFAULT_RATES.has(s.rate) ? { ...s, rate: fx.rate } : s));
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const setCurrency = useCallback((c: string, r: number, p: string) => {
    setState({ code: c, rate: r, prefix: p });
  }, []);

  const value = useMemo<CurrencyContextValue>(
    () => ({ code, rate, prefix, setCurrency }),
    [code, rate, prefix, setCurrency]
  );

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
}

/** Subscribe to currency changes — call from any component that displays money
 *  via fmtUSD/fmtUSDc to ensure it re-renders when the user changes currency. */
export function useCurrency(): CurrencyContextValue {
  return useContext(CurrencyContext);
}
